import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Header, ListBox, Separator } from '@heroui/react';
import { CategoryPickerOption } from './CategoryPickerOption';
import {
    NO_CATEGORY_KEY,
    TICKED,
    UNTICKED,
    categoryTreeSections,
    type CategoryTickState,
    type CategoryTree,
} from './categoryPickerMeta';

type CategoryBrowseOptionsProps = {
    tree: CategoryTree;
    tickStates: ReadonlyMap<string, CategoryTickState>;
    /** Whether "Uncategorized" is picked. */
    noCategory: boolean;
};

/**
 * The category picker's list while nothing is typed (design board
 * **CategoryPicker**): "Uncategorized" pinned on top, then one section per
 * top-level category, headed by its name. The section's first row picks the
 * whole branch ("All of Electronics", partly ticked "2 of 19" while only some
 * of it is picked); the rows under it step in one level per level of the
 * tree. A top-level category with nothing under it is one row, not a section
 * of one.
 */
export const CategoryBrowseOptions: React.FC<CategoryBrowseOptionsProps> = ({
    tree,
    tickStates,
    noCategory,
}) => {
    const { t } = useTranslation('pages');
    const tickOf = (id: string) => tickStates.get(id) ?? UNTICKED;
    const uncategorized = t(
        'products.components.productTable.filters.uncategorized',
    );

    return (
        <>
            <CategoryPickerOption
                id={NO_CATEGORY_KEY}
                label={uncategorized}
                tick={noCategory ? TICKED : UNTICKED}
            />
            <Separator />
            {categoryTreeSections(tree).map(({ root, descendants }) => {
                if (descendants.length === 0) {
                    return (
                        <CategoryPickerOption
                            id={root.id}
                            key={root.id}
                            label={root.name}
                            tick={tickOf(root.id)}
                        />
                    );
                }
                return (
                    <ListBox.Section key={root.id}>
                        <Header>{root.name}</Header>
                        <CategoryPickerOption
                            id={root.id}
                            label={t(
                                'products.components.productTable.filters.allIn',
                                { name: root.name },
                            )}
                            tick={tickOf(root.id)}
                        />
                        {descendants.map((node) => (
                            <CategoryPickerOption
                                id={node.id}
                                indent={node.depth}
                                key={node.id}
                                label={node.name}
                                tick={tickOf(node.id)}
                            />
                        ))}
                    </ListBox.Section>
                );
            })}
        </>
    );
};
